import React, { useState, useEffect, useMemo } from "react";
import {
    Modal,
    Flex,
    Tabs,
    Avatar,
    Badge,
    Button,
    Space,
    Typography,
    Tooltip,
    Divider,
    Empty,
    message,
    theme,
} from "antd";
import { GlassCard } from "../../../shared/components";
import {
    UserOutlined,
    TeamOutlined,
    ShopOutlined,
    CheckOutlined,
} from "@ant-design/icons";
import { TunerRole } from "../../../models/user";
import { useAvatarPalette } from "../../../shared/styles";
import { getAvatarColor, getInitials, getInitialsFromName } from "../../../shared/utils";

const AssignmentPanel = ({ 
    assignedUserId, 
    assignedServiceProviderId,
    assignedGroupId,
    assignmentStatus,
    createdAt,
    usersData,
    serviceProvidersData,
    groupsData,
    update,
    invalidate,
    record,
    isAdmin,
    _t,
    onClose,
    onSuccess,
    mode = "order",
    onAssignForRule,
}: {
    assignedUserId?: number;
    assignedServiceProviderId?: number;
    assignedGroupId?: number;
    assignmentStatus?: "pending" | "assigned";
    createdAt?: string | Date;
    usersData?: any;
    serviceProvidersData?: any;
    groupsData?: any;
    update?: any;
    invalidate?: any;
    record?: any;
    isAdmin: boolean;
    _t: (key: string) => string;
    onClose?: () => void;
    onSuccess?: () => void;
    mode?: "order" | "rule";
    onAssignForRule?: (assignmentType: "tuner" | "group" | "provider", targetId: number) => void;
}) => {
    const { token } = theme.useToken();
    const avatarPalette = useAvatarPalette();
    const [activeTab, setActiveTab] = useState<string>('tuner');
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [selectedType, setSelectedType] = useState<'tuner' | 'group' | 'provider' | null>(null);
    const [isAssigning, setIsAssigning] = useState(false);
    const [elapsedTime, setElapsedTime] = useState<string>("00:00");
    
    // Timer to calculate elapsed time from order creation (order mode only)
    useEffect(() => {
        if (mode === "rule" || !createdAt || assignmentStatus !== "pending") {
            setElapsedTime("00:00");
            return;
        }

        const updateTimer = () => {
            const now = new Date().getTime();
            const created = new Date(createdAt).getTime();
            const diff = Math.floor((now - created) / 1000);

            const minutes = Math.floor(diff / 60);
            const seconds = diff % 60;
            const formattedTime = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
            setElapsedTime(formattedTime);
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [mode, createdAt, assignmentStatus]);
    
    // Filter tuners from users data
    const tuners = useMemo(() => {
        return usersData?.data?.filter((user: any) => 
            user.role?.id === TunerRole.id || user.role?.id === 5
        ) || [];
    }, [usersData]);

    // Get current assignee name
    const getAssigneeName = () => {
        if (assignedUserId) {
            const user = tuners.find((u: any) => u.id === assignedUserId);
            if (user) {
                const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
                return name || user.email || `User ${user.id}`;
            }
        }
        if (assignedGroupId) {
            const group = groupsData?.data?.find((g: any) => g.id === assignedGroupId);
            if (group) {
                return group.name;
            }
        }
        if (assignedServiceProviderId) {
            const provider = serviceProvidersData?.data?.find((p: any) => p.id === assignedServiceProviderId);
            if (provider) {
                return provider.name;
            }
        }
        return null;
    };

    const assigneeName = getAssigneeName();
    const isAssigned = mode === "rule"
        ? !!(assignedUserId || assignedGroupId || assignedServiceProviderId)
        : assignmentStatus === "assigned" && (assignedUserId || assignedGroupId || assignedServiceProviderId);

    // Handle card selection
    const handleSelect = (id: number, type: 'tuner' | 'group' | 'provider') => {
        if (selectedId === id && selectedType === type) {
            setSelectedId(null);
            setSelectedType(null);
        } else {
            setSelectedId(id);
            setSelectedType(type);
        }
    };

    // Handle assignment
    const handleAssignment = async () => {
        if (!selectedId || !selectedType) return;
        
        if (mode === "rule" && onAssignForRule) {
            setIsAssigning(true);
            try {
                onAssignForRule(selectedType, selectedId);
                message.success(_t("assignment-rules.messages.assigneeUpdated"));
                onClose?.();
            } finally {
                setIsAssigning(false);
            }
            return;
        }
        
        setIsAssigning(true);
        
        const values: any = {
            assignedUserId: null,
            assignedGroupId: null,
            serviceProviderId: null,
            assignmentStatus: "pending"
        };
        
        if (selectedType === 'tuner') {
            values.assignedUserId = selectedId;
            values.assignmentStatus = "assigned";
        } else if (selectedType === 'group') {
            values.assignedGroupId = selectedId;
            values.assignmentStatus = "assigned";
        } else if (selectedType === 'provider') {
            values.serviceProviderId = selectedId;
            values.assignmentStatus = "assigned";
        }
        
        update?.({
            resource: 'orders',
            values,
            id: record?.id,
        }, {
            onSuccess: () => {
                setIsAssigning(false);
                invalidate?.({ resource: 'orders', invalidates: ['detail'] });
                if (selectedType === 'tuner') {
                    message.success(_t("orders.titles.userAssigned"));
                } else if (selectedType === 'group') {
                    message.success(_t("orders.titles.groupAssigned"));
                } else {
                    message.success(_t("orders.titles.serviceProviderAssigned"));
                }
                onSuccess?.();
                onClose?.(); // Close modal on success
            },
            onError: () => {
                setIsAssigning(false);
                if (selectedType === 'tuner') {
                    message.error(_t("orders.titles.failedToAssignUser"));
                } else if (selectedType === 'group') {
                    message.error(_t("orders.titles.failedToAssignGroup"));
                } else {
                    message.error(_t("orders.titles.failedToAssignServiceProvider"));
                }
            }
        });
    };

    if (!isAdmin) {
        return null;
    }

    // Tuner Card Component
    const TunerCard = ({ tuner }: { tuner: any }) => {
        const isSelected = selectedId === tuner.id && selectedType === 'tuner';
        return (
            <div
                role="button"
                tabIndex={0}
                onClick={() => handleSelect(tuner.id, 'tuner')}
                onKeyDown={(e) => e.key === 'Enter' && handleSelect(tuner.id, 'tuner')}
                className={`mb-3 cursor-pointer transition-all ${isSelected ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : ''}`}
            >
                <GlassCard className="p-4">
                <Flex gap={12} align="start">
                    <Avatar
                        size={48}
                        shape="square"
                        style={{ backgroundColor: getAvatarColor(tuner.id, avatarPalette), fontWeight: 600 }}
                    >
                        {getInitials(tuner.firstName, tuner.lastName)}
                    </Avatar>
                    <Flex vertical style={{ flex: 1 }} gap={4}>
                        <Flex justify="space-between" align="center">
                            <Typography.Text strong>
                                {`${tuner.firstName || ''} ${tuner.lastName || ''}`.trim() || tuner.email || `User ${tuner.id}`}
                            </Typography.Text>
                            {isSelected && <CheckOutlined style={{ color: token.colorPrimary }} />}
                        </Flex>
                        {tuner.role?.name && (
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                {tuner.role.name}
                            </Typography.Text>
                        )}
                    </Flex>
                </Flex>
                </GlassCard>
            </div>
        );
    };

    // Group Card Component
    const GroupCard = ({ group }: { group: any }) => {
        const isSelected = selectedId === group.id && selectedType === 'group';
        return (
            <div
                role="button"
                tabIndex={0}
                onClick={() => handleSelect(group.id, 'group')}
                onKeyDown={(e) => e.key === 'Enter' && handleSelect(group.id, 'group')}
                className={`mb-3 cursor-pointer transition-all ${isSelected ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : ''}`}
            >
                <GlassCard className="p-4">
                <Flex gap={12} align="start">
                    <Avatar
                        size={48}
                        shape="square"
                        style={{ backgroundColor: getAvatarColor(group.id, avatarPalette), fontWeight: 600 }}
                    >
                        {getInitialsFromName(group.name)}
                    </Avatar>
                    <Flex vertical style={{ flex: 1 }} gap={4}>
                        <Flex justify="space-between" align="center">
                            <Typography.Text strong>{group.name}</Typography.Text>
                            {isSelected && <CheckOutlined style={{ color: token.colorPrimary }} />}
                        </Flex>
                        {group.comments && (
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                {group.comments}
                            </Typography.Text>
                        )}
                        {group.users && (
                            <Flex gap={4} align="center" style={{ marginTop: 4 }}>
                                <Avatar.Group size="small" max={{ count: 3 }}>
                                    {group.users.slice(0, 3).map((user: any, i: number) => (
                                        <Tooltip key={i} title={`${user.firstName || ''} ${user.lastName || ''}`.trim()}>
                                            <Avatar size="small" shape="square" style={{ backgroundColor: getAvatarColor(user.id, avatarPalette) }}>
                                                {getInitials(user.firstName, user.lastName)}
                                            </Avatar>
                                        </Tooltip>
                                    ))}
                                </Avatar.Group>
                                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                                    {group.users.length} {group.users.length === 1 ? _t("user-groups.fields.users")?.slice(0, -1) : _t("user-groups.fields.users")}
                                </Typography.Text>
                            </Flex>
                        )}
                    </Flex>
                </Flex>
                </GlassCard>
            </div>
        );
    };

    // Provider Card Component
    const ProviderCard = ({ provider }: { provider: any }) => {
        const isSelected = selectedId === provider.id && selectedType === 'provider';
        return (
            <div
                role="button"
                tabIndex={0}
                onClick={() => handleSelect(provider.id, 'provider')}
                onKeyDown={(e) => e.key === 'Enter' && handleSelect(provider.id, 'provider')}
                className={`mb-3 cursor-pointer transition-all ${isSelected ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : ''}`}
            >
                <GlassCard className="p-4">
                <Flex gap={12} align="start">
                    <Avatar
                        size={48}
                        shape="square"
                        style={{ backgroundColor: getAvatarColor(provider.id, avatarPalette), fontWeight: 600 }}
                    >
                        {getInitialsFromName(provider.name)}
                    </Avatar>
                    <Flex vertical style={{ flex: 1 }} gap={4}>
                        <Flex justify="space-between" align="center">
                            <Typography.Text strong>{provider.name}</Typography.Text>
                            {isSelected && <CheckOutlined style={{ color: token.colorPrimary }} />}
                        </Flex>
                    </Flex>
                </Flex>
                </GlassCard>
            </div>
        );
    };

    // Tab items
    const tabItems = [
        {
            key: 'tuner',
            label: (
                <Space>
                    <UserOutlined />
                    <span>{_t("orders.titles.tuner")}</span>
                    <Badge count={tuners.length} showZero size="small" style={{ backgroundColor: token.colorPrimary }} />
                </Space>
            ),
            children: (
                <Flex vertical gap={0} style={{ maxHeight: 400, overflowY: 'auto', paddingRight: 8 }}>
                    {tuners.length > 0 ? (
                        tuners.map((tuner: any) => (
                            <TunerCard key={tuner.id} tuner={tuner} />
                        ))
                    ) : (
                        <Empty description={_t("orders.titles.selectUser")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    )}
                </Flex>
            ),
        },
        {
            key: 'group',
            label: (
                <Space>
                    <TeamOutlined />
                    <span>{_t("orders.titles.group")}</span>
                    <Badge count={groupsData?.data?.length || 0} showZero size="small" style={{ backgroundColor: token.colorPrimary }} />
                </Space>
            ),
            children: (
                <Flex vertical gap={0} style={{ maxHeight: 400, overflowY: 'auto', paddingRight: 8 }}>
                    {groupsData?.data && groupsData.data.length > 0 ? (
                        groupsData.data.map((group: any) => (
                            <GroupCard key={group.id} group={group} />
                        ))
                    ) : (
                        <Empty description={_t("orders.titles.selectGroup")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    )}
                </Flex>
            ),
        },
        {
            key: 'provider',
            label: (
                <Space>
                    <ShopOutlined />
                    <span>{_t("orders.titles.serviceProvider")}</span>
                    <Badge count={serviceProvidersData?.data?.length || 0} showZero size="small" style={{ backgroundColor: token.colorPrimary }} />
                </Space>
            ),
            children: (
                <Flex vertical gap={0} style={{ maxHeight: 400, overflowY: 'auto', paddingRight: 8 }}>
                    {serviceProvidersData?.data && serviceProvidersData.data.length > 0 ? (
                        serviceProvidersData.data.map((provider: any) => (
                            <ProviderCard key={provider.id} provider={provider} />
                        ))
                    ) : (
                        <Empty description={_t("orders.titles.selectServiceProvider")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    )}
                </Flex>
            ),
        },
    ];

    return (
        <Flex vertical gap="middle">
            {assigneeName && (
                <Flex align="center" gap="small">
                    <Typography.Text strong>{_t("orders.titles.currentAssignee")}:</Typography.Text>
                    <Typography.Text>
                        {assignedUserId ? <UserOutlined /> : assignedGroupId ? <TeamOutlined /> : <ShopOutlined />} {assigneeName}
                    </Typography.Text>
                </Flex>
            )}
            {mode === "rule" && !assigneeName && (
                <Typography.Text type="secondary">{_t("assignment-rules.fields.selectAssignee")}</Typography.Text>
            )}
            
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
                size="small"
            />

            <Divider className="my-1"/>

            <Flex justify="flex-end">
                <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    disabled={!selectedId || !selectedType}
                    loading={isAssigning}
                    onClick={handleAssignment}
                    size="middle"
                >
                    {_t("orders.titles.assign")}
                </Button>
            </Flex>
        </Flex>
    );
};

export const AssignmentModal = ({
    open,
    onCancel,
    assignedUserId,
    assignedServiceProviderId,
    assignedGroupId,
    assignmentStatus,
    createdAt,
    usersData,
    serviceProvidersData,
    groupsData,
    update,
    invalidate,
    record,
    isAdmin,
    _t,
    mode = "order",
    onSuccess,
    onAssignForRule,
}: {
    open: boolean;
    onCancel: () => void;
    assignedUserId?: number;
    assignedServiceProviderId?: number;
    assignedGroupId?: number;
    assignmentStatus?: "pending" | "assigned";
    createdAt?: string | Date;
    usersData?: any;
    serviceProvidersData?: any;
    groupsData?: any;
    update?: any;
    invalidate?: any;
    record?: any;
    isAdmin: boolean;
    _t: (key: string) => string;
    mode?: "order" | "rule";
    onSuccess?: () => void;
    onAssignForRule?: (assignmentType: "tuner" | "group" | "provider", targetId: number) => void;
}) => {
    const modalTitle = mode === "rule"
        ? _t("assignment-rules.fields.assignmentType")
        : _t("orders.titles.assignment");
    return (
        <Modal
            title={modalTitle}
            open={open}
            onCancel={onCancel}
            width={600}
            footer={null}
        >
            <AssignmentPanel
                assignedUserId={assignedUserId}
                assignedServiceProviderId={assignedServiceProviderId}
                assignedGroupId={assignedGroupId}
                assignmentStatus={assignmentStatus}
                createdAt={createdAt}
                usersData={usersData}
                serviceProvidersData={serviceProvidersData}
                groupsData={groupsData}
                update={update}
                invalidate={invalidate}
                record={record}
                isAdmin={isAdmin}
                _t={_t}
                onClose={onCancel}
                onSuccess={onSuccess}
                mode={mode}
                onAssignForRule={onAssignForRule}
            />
        </Modal>
    );
};
